import { useContext, useEffect } from "react";
import { getEpoch, getTodayWithTime } from "./DateFormatter";

import OpenEnrollmentDao from "../dao/OpenEnrollmentDao";
import { GlobalContext } from "../components/contextProvider/ContextProvider";

/**
 * Do the start and end dates encompass now?
 * @name isWindowOpen
 * @param {string} start
 * @param {string} end
 * @returns {bool}
 */
export const isWindowOpen = (start, end) => {
    const epochNow = getEpoch(getTodayWithTime());
    if (start && end) {
        return getEpoch(start) <= epochNow && getEpoch(end) > epochNow;
    }

    return false;
};

/**
 * Is the districtWindow open?
 * @name isRoundOpen
 * @param {{}} districtWindow
 * @returns {bool}
 */
export const isRoundOpen = (districtWindow) => {
    if (districtWindow) {
        return isWindowOpen(districtWindow.startDate, districtWindow.endDate);
    }

    return false;
};

/**
 * Return the requested action control object
 * @name getActionControl
 * @param {string} action
 * @param {[]} actionControls
 * @param {{}} districtWindow
 * @returns {{}|null}
 */
export const getActionControl = (action, actionControls, districtWindow) => {
    const result = actionControls.filter(
        (obj) => obj.action === action && parseInt(obj.differentiator, 10) === parseInt(districtWindow.key, 10)
    );
    if (result && result.length > 0) {
        return result[0];
    }

    return null;
};

/**
 * is the specific action control open?
 * @name isAllowed
 * @param {string} action
 * @param {[]} actionControls
 * @param {{}} activeDistrictWindow
 * @param {[]} districtWindows
 * @returns {bool}
 */
export const isAllowed = (action, actionControls, activeDistrictWindow, districtWindows) => {
    if (!isRoundOpen(districtWindows[0]) && !isRoundOpen(districtWindows[1])) {
        return false;
    }

    const actionControl = getActionControl(action, actionControls, activeDistrictWindow);

    if (actionControl && isWindowOpen(actionControl.startDate, actionControl.endDate)) {
        return true;
    }

    return false;
};

/**
 * Is the PARENT_REQUEST window open for this round?
 * @name parentRequestAllowed
 * @param {[]} actionControls
 * @param {{}} activeDistrictWindow
 * @param {[]} districtWindows
 * @returns {bool}
 */
export const parentRequestAllowed = (actionControls, activeDistrictWindow, districtWindows) => {
    if (!isRoundOpen(districtWindows[0]) && !isRoundOpen(districtWindows[1])) {
        return false;
    }

    const parentRequest = getActionControl("PARENT_REQUEST", actionControls, activeDistrictWindow);

    if (isWindowOpen(parentRequest.startDate, parentRequest.endDate)) {
        return true;
    }

    return false;
};

/**
 * Specifically, are we between PARENT_REQUEST windows?
 * @name parentRequestBetweenRounds
 * @param {[]} actionControls
 * @param {{}} districtWindows
 * @returns {bool}
 */
export const parentRequestBetweenRounds = (actionControls, districtWindows) => {
    if (!isRoundOpen(districtWindows[0]) && !isRoundOpen(districtWindows[1])) {
        return false;
    }
    const parentRequest = getActionControl("PARENT_REQUEST", actionControls, districtWindows[0]); //getActionControlsWrapper(\Inspired\OpenEnrollment\DistrictWindowDao::getActiveSchoolYearKey(), \Inspired\OpenEnrollment\ActionControlType::PARENT_REQUEST, $windows['ROUND1']['key']);

    const parentRequestRoundTwo = getActionControl("PARENT_REQUEST", actionControls, districtWindows[1]); // ActionControlsDao::getActionControlsWrapper(\Inspired\OpenEnrollment\DistrictWindowDao::getActiveSchoolYearKey(), \Inspired\OpenEnrollment\ActionControlType::PARENT_REQUEST, $windows['ROUND2']['key']);

    if (isWindowOpen(parentRequest.endDate, parentRequestRoundTwo.startDate)) {
        return true;
    }

    return false;
};

/**
 * Specifically determine if the current time is between finalize round1 and parent request round2
 * @name parentViewBetweenRounds
 * @param {[]} actionControls
 * @param {[]} districtWindows
 * @returns {bool}
 */
export const parentViewBetweenRounds = (actionControls, districtWindows) => {
    if (!isRoundOpen(districtWindows[0]) && !isRoundOpen(districtWindows[1])) {
        return false;
    }
    const schoolFinalizeRoundOne = getActionControl("SCHOOL_FINALIZE", actionControls, districtWindows[0]); // ActionControlsDao::getActionControlsWrapper(\Inspired\OpenEnrollment\DistrictWindowDao::getActiveSchoolYearKey(), \Inspired\OpenEnrollment\ActionControlType::SCHOOL_FINALIZE, $windows['ROUND1']['key']);

    const parentRequestRoundTwo = getActionControl("PARENT_REQUEST", actionControls, districtWindows[1]); // ActionControlsDao::getActionControlsWrapper(\Inspired\OpenEnrollment\DistrictWindowDao::getActiveSchoolYearKey(), \Inspired\OpenEnrollment\ActionControlType::PARENT_REQUEST, $windows['ROUND2']['key']);

    if (isWindowOpen(schoolFinalizeRoundOne.startDate, parentRequestRoundTwo.startDate)) {
        return true;
    }

    return false;
};

/**
 * Specifically determine if the SCHOOL_VIEW_PARENT_OFFER window is open
 * @name schoolViewParentOfferAllowed
 * @param {[]} actionControls
 * @param {{}} activeDistrictWindow
 * @param {[]} districtWindows
 * @returns {bool}
 */
export const schoolViewParentOfferAllowed = (actionControls, activeDistrictWindow, districtWindows) => {
    if (!isRoundOpen(districtWindows[0]) && !isRoundOpen(districtWindows[1])) {
        return false;
    }

    const schoolViewParentOffer = getActionControl("SCHOOL_VIEW_PARENT_OFFER", actionControls, activeDistrictWindow);
    if (isWindowOpen(schoolViewParentOffer.startDate, schoolViewParentOffer.endDate)) {
        return true;
    }

    return false;
};

/**
 * Specifically determine if the SCHOOL_VIEW_ONLY window is open
 * @name schoolViewOnlyAllowed
 * @param {[]} actionControls
 * @param {{}} activeDistrictWindow
 * @param {[]} districtWindows
 * @returns {bool}
 */
export const schoolViewOnlyAllowed = (actionControls, activeDistrictWindow, districtWindows) => {
    if (!isRoundOpen(districtWindows[0]) && !isRoundOpen(districtWindows[1])) {
        return false;
    }
    const schoolViewOnly = getActionControl("SCHOOL_VIEW_ONLY", actionControls, activeDistrictWindow);
    if (isWindowOpen(schoolViewOnly.startDate, schoolViewOnly.endDate)) {
        return true;
    }

    return false;
};

/**
 * @description Construct an object of windows for the OE App
 * @name getOeWindow
 * @returns {{}|null} data
 */
const getOeWindows = (oeActionControls, oeActiveDistrictWindow, oeDistrictWindows) => {
    try {
        if (oeActionControls && oeActiveDistrictWindow && oeDistrictWindows) {
            const data = {};
            const adminOfferConfirmAllowed = isAllowed(
                "ADMIN_OFFER_CONFIRM",
                oeActionControls,
                oeActiveDistrictWindow,
                oeDistrictWindows
            );
            const schoolOfferPendingAllowed = isAllowed(
                "SCHOOL_OFFER_PENDING",
                oeActionControls,
                oeActiveDistrictWindow,
                oeDistrictWindows
            );
            data.isAdminOfferConfirmAllowed = adminOfferConfirmAllowed;
            data.isParentOfferAllowed = isAllowed(
                "PARENT_OFFER",
                oeActionControls,
                oeActiveDistrictWindow,
                oeDistrictWindows
            );
            data.isParentRequestAllowed = isAllowed(
                "PARENT_REQUEST",
                oeActionControls,
                oeActiveDistrictWindow,
                oeDistrictWindows
            );
            data.isParentRequestBetweenRoundsFlag = parentRequestBetweenRounds(oeActionControls, oeDistrictWindows);
            data.isParentRequestRound2Allowed = isAllowed(
                "PARENT_REQUEST",
                oeActionControls,
                oeDistrictWindows[1],
                oeDistrictWindows
            );
            data.isParentViewBetweenRounds = parentViewBetweenRounds(oeActionControls, oeDistrictWindows);
            data.isRound1 = isRoundOpen(oeDistrictWindows[0]);
            data.isRound2 = isRoundOpen(oeDistrictWindows[1]);
            data.isSchoolFinalizeAllowed = isAllowed(
                "SCHOOL_FINALIZE",
                oeActionControls,
                oeActiveDistrictWindow,
                oeDistrictWindows
            );
            data.isSchoolOfferAllowed = adminOfferConfirmAllowed || schoolOfferPendingAllowed;
            data.isSchoolOfferPendingAllowed = schoolOfferPendingAllowed;
            data.isSchoolYearExtensionWindowAllowed = isAllowed(
                "SCHOOL_YEAR_EXTENSION_WINDOW",
                oeActionControls,
                oeDistrictWindows[1],
                oeDistrictWindows
            );
            data.parentOffer = getActionControl("PARENT_OFFER", oeActionControls, oeActiveDistrictWindow);
            data.parentRequest = getActionControl("PARENT_REQUEST", oeActionControls, oeActiveDistrictWindow);
            data.parentRequestRound2 = getActionControl("PARENT_REQUEST", oeActionControls, oeDistrictWindows[1]);
            data.schoolFinalize = getActionControl("SCHOOL_FINALIZE", oeActionControls, oeActiveDistrictWindow);

            return data;
        }
    } catch (error) {
        console.error("Unable to set Open Enrollment permission windows:\n", error);
    }

    return null;
};

export const useActionControls = () => {
    const { dispatch, state } = useContext(GlobalContext);
    const { oeActiveDistrictWindow, oeAllActionControls, oeDistrictWindows, oeSchoolYearKey, token } = state || {};

    /**
     * Get the current school year
     */
    useEffect(() => {
        if (oeSchoolYearKey || !token) {
            return;
        }

        /**
         * @name fetchSchoolYear
         * @description Get the current school year
         * @async
         */
        const fetchOeSchoolYearKey = async () => {
            try {
                const options = {
                    action: "oeSchoolYearRead",
                    token
                };
                const response = await OpenEnrollmentDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    dispatch({
                        type: "OeSchoolYearKey",
                        oeSchoolYearKey: payload
                    });
                }
            } catch (error) {
                console.error("Unable to retrieve the current school year:\n", error);
            }
        };

        fetchOeSchoolYearKey();
    }, [dispatch, oeSchoolYearKey, token]);

    /**
     * Set District Windows and the Active District Window if it exists
     */
    useEffect(() => {
        if (oeDistrictWindows || !oeSchoolYearKey || !token) {
            return;
        }

        /**
         * @name fetchDistictWindows
         * @description Get a list of all District Windows for the school year
         * @async
         */
        const fetchDistictWindows = async () => {
            try {
                const options = {
                    action: "oeAllDistrictWindowsRead",
                    schoolYearKey: oeSchoolYearKey,
                    token
                };
                const response = await OpenEnrollmentDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    payload.sort((a, b) => {
                        return a.enrollmentRound > b.enrollmentRound ? 1 : -1;
                    });
                    dispatch({
                        type: "OeDistrictWindows",
                        oeDistrictWindows: payload
                    });
                    // get the activeDistrictWindow
                    const epochNow = getEpoch(getTodayWithTime());
                    const activeMatch = payload.filter(
                        (obj) => getEpoch(obj.startDate) <= epochNow && getEpoch(obj.endDate) >= epochNow
                    );
                    if (activeMatch && activeMatch.length > 0) {
                        dispatch({
                            type: "OeActiveDistrictWindow",
                            oeActiveDistrictWindow: activeMatch[0]
                        });
                    } else {
                        dispatch({
                            type: "OeActiveDistrictWindow",
                            oeActiveDistrictWindow: null
                        });
                    }
                }
            } catch (error) {
                console.error(`Unable to retrieve Active District Window for school year ${oeSchoolYearKey}:\n`, error);
            }
        };

        fetchDistictWindows();
    }, [dispatch, oeDistrictWindows, oeSchoolYearKey, token]);

    /**
     * Get a list of all Action Control windows for the school year
     */
    useEffect(() => {
        if (oeAllActionControls || !token || !oeActiveDistrictWindow || !oeSchoolYearKey) {
            return;
        }

        // if we don't have an active district window, use the school year
        const syk = oeActiveDistrictWindow ? oeActiveDistrictWindow?.schoolYearKey : oeSchoolYearKey;

        /**
         * @name fetchActionControls
         * @description Get a list of all Action Control windows for the school year
         * @async
         */
        const fetchAllActionControls = async () => {
            try {
                const options = {
                    action: "oeAllActionControlsRead",
                    params: {
                        schoolYearKey: syk
                    },
                    token
                };
                const response = await OpenEnrollmentDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    dispatch({
                        type: "OeAllActionControls",
                        oeAllActionControls: payload
                    });
                }
            } catch (error) {
                console.error(`Unable to retrieve OE Action Controls for school year ${syk}:\n`, error);
            }
        };

        fetchAllActionControls();
    }, [oeAllActionControls, oeActiveDistrictWindow, dispatch, oeSchoolYearKey, token]);

    useEffect(() => {
        if (!oeAllActionControls || !oeActiveDistrictWindow || !oeDistrictWindows) {
            return;
        }
        try {
            const data = getOeWindows(oeAllActionControls, oeActiveDistrictWindow, oeDistrictWindows);
            dispatch({
                type: "OeWindows",
                oeWindows: data
            });
            dispatch({
                type: "AdwCalled",
                adwCalled: true
            });
        } catch (error) {
            console.error("Unable to set Open Enrollment permission windows:\n", error);
        }
    }, [oeAllActionControls, oeActiveDistrictWindow, oeDistrictWindows, dispatch]);
};
